// @ts-strict-ignore
import React, { FC, useEffect, useState, useRef } from 'react'
import classNames from 'classnames'
import { useMediaQuery } from 'react-responsive'
import { SectionTextContainer as Container } from 'mytours-read-common'
import { Button, Text } from '@mytours/components'
import { getDirMode } from '../../../platform'
import strings from '../../../../strings'
import { getCollapseShadowStyle } from '../../functions'
import '../styles/sectionText.less'
import '../styles/sectionCollapsable.less'

const SectionText: FC<Container.Props & Container.Actions> = ({ section, isInsideModal }) => {
	const [isCollapsed, setCollapsed] = useState(false)
	const [blockHeight, setBlockHeight] = useState<number>()
	const contentRef = useRef<HTMLDivElement>()

	const isTabletOrMobile = useMediaQuery({ maxWidth: 1024.5 })

	useEffect(() => {
		const interval = setInterval(() => {
			if (contentRef?.current?.clientHeight !== 0 && !blockHeight && section?.collapsable) {
				setBlockHeight(contentRef?.current.clientHeight * section.exposable_content * 0.01)
				setCollapsed(section.exposable_content !== 100)
				clearInterval(interval)
			}
		}, 50)

		return () => clearInterval(interval)
	}, [contentRef?.current, section])

	const renderCollapseButton = (): React.ReactElement => (
		<Button onClick={() => setCollapsed(!isCollapsed)} className="collapse-btn" variant="clear" dir={getDirMode()}>
			<i className={`fas fa-caret-${isCollapsed ? 'down' : 'up'}`} />
			<span>{isCollapsed ? strings.expand : strings.collapse}</span>
		</Button>
	)

	const { collapsable, exposable_content: exposableContent } = section
	const isFullyCollapsed = collapsable && (exposableContent === 0 || exposableContent === 100 || blockHeight < 85)

	const getCollapsedStyles = () => {
		if (isCollapsed && exposableContent !== 0) {
			return {
				maxHeight: `${exposableContent === 100 ? 34 : blockHeight}px`,
				overflow: 'hidden',
				height: `${exposableContent === 100 ? 34 : blockHeight}px`,
			}
		}
	}

	const renderHeading = (): React.ReactElement => (
		<div className="block-header heading">
			{section?.title && (
				<h2 className={`subheading ${isCollapsed ? 'subheading-no-overflow' : ''}`}>
					<Text variant="inherit" data-cy="story-screen-section-heading">
						{section.title}
					</Text>
				</h2>
			)}
			{section?.subtitle && (
				<Text variant="inherit" className="subtitle-text">
					{section.subtitle}
				</Text>
			)}
		</div>
	)

	return (
		<div
			dir={getDirMode()}
			className={classNames('section-text', {
				'section-collapsable': collapsable,
				'-collapsed': isCollapsed,
				'-fully-collapsable': isFullyCollapsed,
			})}
			style={getCollapsedStyles()}
			ref={contentRef}
		>
			{renderHeading()}
			{isFullyCollapsed && renderCollapseButton()}
			{section?.body && ((isFullyCollapsed && !isCollapsed) || !isFullyCollapsed) && (
				<div className="block-content body-text" data-cy="story-screen-section-content">
					<div
						style={{ height: '100%', maxHeight: '100%', overflow: 'hidden' }}
						dangerouslySetInnerHTML={{ __html: section.body }}
					/>
					{collapsable && exposableContent !== 0 && exposableContent !== 100 && blockHeight >= 85 && (
						<div
							className={classNames('collapse-shadow', {
								'mobile-or-tablet-collapsed': (isTabletOrMobile || isInsideModal) && isCollapsed,
							})}
							style={{ background: getCollapseShadowStyle() }}
						>
							{renderCollapseButton()}
						</div>
					)}
				</div>
			)}
		</div>
	)
}

const SectionTextComponent = Container.default(SectionText)
export default SectionTextComponent
